import React, { useEffect, useState } from "react";
import { formatNumber } from "../../../app/formatters/NumberFormatter";

/** @typedef {import('../models/RpaLocationModel.js').RpaLocationItem} RpaLocationItem */

/**
 * @typedef {object} RpaLocationParams
 * @property {Array<RpaLocationItem>} targetedRpaLocations - Liste des locations par défaut ciblé pour la réservation en fonction du nombre de personnes total de la réservation
 * @property {Array<RpaLocationItem>} rpaLocations - Liste de toutes les locations disponibles sur la resource ou l'activité
 * @property {boolean} autoLocationAssignement - Si l'assignement automatique de location est activé ou non sur la resource
 * @property {number} nbTotalPeoples - Nombre total de personnes de la réservation
 */

/**
 * @param {RpaLocationParams} props
 */
export default function RpaLocation({ targetedRpaLocations, rpaLocations, autoLocationAssignement, nbTotalPeoples }) {
  /**
   * @param {Array<string | number>} nextSelections
   * @param {number} [lockedIndex=-1]
   * @returns {Array<string>}
   */
  const normalizeSelections = (nextSelections, lockedIndex = -1) => {
    const remainingStockById = new Map(rpaLocations.filter(location => Number(location.real_sock_during_day) > 0).map(location => [String(location.id), Number(location.real_sock_during_day) || 0]));
    const normalizedSelections = Array(targetedRpaLocations.length).fill("");

    // Keep the user-edited card first so changing the last card can rebalance previous ones.
    if (lockedIndex >= 0 && lockedIndex < targetedRpaLocations.length) {
      const lockedDesiredId = String(nextSelections[lockedIndex] ?? "");

      if (lockedDesiredId && (remainingStockById.get(lockedDesiredId) || 0) > 0) {
        normalizedSelections[lockedIndex] = lockedDesiredId;
        remainingStockById.set(lockedDesiredId, (remainingStockById.get(lockedDesiredId) || 0) - 1);
      }
    }

    for (let index = 0; index < targetedRpaLocations.length; index++) {
      if (index === lockedIndex && normalizedSelections[index]) continue;

      const desiredId = String(nextSelections[index] ?? "");
      let selectedId = "";

      // Keep requested choice when stock is still available.
      if (desiredId && (remainingStockById.get(desiredId) || 0) > 0) {
        selectedId = desiredId;
      }

      if (!selectedId) {
        const nextAvailable = Array.from(remainingStockById.entries()).find(([, stock]) => stock > 0);
        selectedId = nextAvailable ? nextAvailable[0] : "";
      }

      if (selectedId) {
        remainingStockById.set(selectedId, (remainingStockById.get(selectedId) || 0) - 1);
      }

      normalizedSelections[index] = selectedId;
    }

    return normalizedSelections;
  };

  const [selectedLocationIds, setSelectedLocationIds] = useState(() => normalizeSelections(targetedRpaLocations.map(location => String(location.id))));

  useEffect(() => {
    setSelectedLocationIds(normalizeSelections(targetedRpaLocations.map(location => String(location.id))));
  }, [targetedRpaLocations, rpaLocations]);

  /**
   * @param {number} index
   * @param {string | number} nextLocationId
   */
  const onChangeLocation = (index, nextLocationId) => {
    const nextSelections = [...selectedLocationIds];
    nextSelections[index] = String(nextLocationId);

    setSelectedLocationIds(normalizeSelections(nextSelections, index));
  };

  const nbPersonsAvailablesInLocations = targetedRpaLocations.reduce((sum, rpaLocation) => {
    const availableStock = rpaLocation.nb_persons;
    return sum + availableStock;
  }, 0);

  const hasNotEnoughStock = nbPersonsAvailablesInLocations < Number(nbTotalPeoples);

  return (
    <>
      {hasNotEnoughStock ? (
        <>
          <div className="alert alert--danger">
            <span className="material-symbols-outlined">error</span>
            <div className="alert__content">
              <div className="alert__text">Il n'y a pas assez de matériel de location pour toutes les personnes de la réservation.</div>
            </div>
          </div>
          <div className="space--16"></div>
        </>
      ) : (
        ""
      )}

      {autoLocationAssignement ? (
        <>
          <div className="grid grid--has-8-child grid--has-4-child-mobile no-mobile">
            {targetedRpaLocations.map((rpaLocation, index) => (
              <div className="card" key={index}>
                <div className="card__header">
                  <h4>{rpaLocation.name}</h4>
                </div>
                {rpaLocation.thumbnail ? <img className="card__image--contain" src={rpaLocation.thumbnail} alt="" /> : ""}
                {index === 0 ? <input type="hidden" name="rpaLocationId" value={rpaLocation.id} /> : <input type="hidden" name="additionalRpaLocationIds[]" value={rpaLocation.id} />}
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="grid grid--has-6-child grid--has-2-child-mobile no-mobile">
            {targetedRpaLocations.map((rpaLocation, index) => (
              <div className="card" key={index}>
                <div className="card__header">
                  <h4>{(rpaLocations.find(location => String(location.id) === String(selectedLocationIds[index])) || rpaLocation).name}</h4>
                </div>
                <img className="card__image--contain" alt="" src={(rpaLocations.find(location => String(location.id) === String(selectedLocationIds[index])) || rpaLocation).thumbnail || "/images/NO_THUMBNAIL.webp"} />
                <div className="card__description card__description--no-height card__description--center">
                  <select className="input-group__input" value={selectedLocationIds[index] || ""} onChange={event => onChangeLocation(index, event.target.value)} name={index === 0 ? "rpaLocationId" : "additionalRpaLocationIds[]"}>
                    {rpaLocations.map(location => {
                      const usedByOthers = selectedLocationIds.filter((id, j) => j !== index && id === String(location.id)).length;
                      const remainingStock = Number(location.real_sock_during_day) - usedByOthers;

                      return (
                        <option key={location.id} value={location.id} disabled={remainingStock <= 0}>
                          {location.name} ({remainingStock > 0 ? remainingStock : 0}) - {formatNumber(location.ttc_price, "€", 2)}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}
